import type { FormsApiClient } from '../api';
import type { LeadFormPublicConfig } from '../types';
import { applyThemeVars, buildCloseButton, buildForm, createHost, resolvePopupStyle, SHARED_PANEL_STYLE, type LeadFormTheme, type RenderHandle } from './shared';

/** A full-width bar pinned to the top or bottom of the viewport — headline + an inline lead-capture
 * form in a single slim row, no room for the image/body/countdown chrome a lightbox has. */
const STYLE = `
  ${SHARED_PANEL_STYLE}
  .panel {
    position: fixed; left: 0; right: 0; z-index: 2147483000;
    border-radius: 0; width: 100%;
    display: flex; align-items: center; justify-content: center; gap: 16px;
    flex-wrap: wrap; padding: 12px 40px; box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  }
  .panel.top { top: 0; }
  .panel.bottom { bottom: 0; }
  .headline { margin: 0; font-size: 15px; white-space: nowrap; }
  form { display: flex; align-items: center; gap: 8px; margin: 0; }
  .cms-lf-field { margin-bottom: 0; }
  .cms-lf-field input, .cms-lf-field select, .cms-lf-field textarea { margin-top: 0; width: auto; }
  .submit { width: auto; margin-top: 0; padding: 8px 16px; white-space: nowrap; }
  .close { position: static; margin-left: 4px; }
`;

// No image/body/countdown/recommendations chrome fits a single-row bar, so this renderer doesn't
// need buildHeader (and therefore no client) — `client` stays in the signature purely so
// render/index.ts's dispatch table can call every renderer the same way.
export function renderRibbon(
  form: LeadFormPublicConfig,
  _client: FormsApiClient,
  onSubmit: (data: Record<string, unknown>) => Promise<boolean>,
): RenderHandle {
  const theme = form.theme as LeadFormTheme;
  const resolved = resolvePopupStyle(theme);
  const { host, root } = createHost(STYLE);

  const panel = document.createElement('div');
  panel.className = `panel ${theme.ribbonPosition === 'bottom' ? 'bottom' : 'top'}`;
  applyThemeVars(panel, resolved);

  if (theme.headline) {
    const headline = document.createElement('p');
    headline.className = 'headline';
    headline.textContent = theme.headline;
    panel.appendChild(headline);
  }

  panel.appendChild(buildForm(form.fields, theme.ctaLabel || 'Submit', onSubmit, close));
  panel.appendChild(buildCloseButton(close));
  root.appendChild(panel);

  function close() {
    host.remove();
  }

  return { close };
}
